import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { MagnifyingGlass, ShieldCheck, Books, Plus, CheckCircle, PencilSimple, Archive, ArrowCounterClockwise, ClockCounterClockwise } from "@phosphor-icons/react";

const SCOPES = [
  ["asset_type", "Asset type"], ["manufacturer", "Manufacturer"], ["model", "Model / variant"],
  ["assets", "Selected assets"], ["asset", "Single asset"],
];

function TargetChip({ v }) {
  return <span className="text-[11px] font-body bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200" data-testid={`qd-target-${v.id}`}>{v.target_display || v.scope_label}</span>;
}

function StatusChip({ status }) {
  const cls = status === "verified" ? "bg-emerald-100 text-emerald-700"
    : status === "superseded" ? "bg-slate-200 text-slate-500"
    : "bg-amber-100 text-amber-700";
  const label = status === "unverified" ? "Awaiting verification" : status;
  return <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

function SearchTab() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setData((await api.get(`/quickdata/search?q=${encodeURIComponent(q.trim())}`)).data); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div className="relative">
        <MagnifyingGlass size={22} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="quickdata-search-input" placeholder='e.g. "wheel torque", "tyre pressure", "oil"'
          value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="pl-11 h-16 text-lg border-2" />
      </div>
      <Button data-testid="quickdata-search-btn" onClick={search} disabled={busy} className="h-12 bg-slate-900 hover:bg-slate-800 font-bold uppercase">Search</Button>
      {data && (
        <div className="flex flex-col gap-3">
          {data.results.map((r) => (
            <div key={r.id} data-testid={`quickdata-result-${r.id}`} className="bg-white border-2 border-slate-300 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-body text-sm text-slate-600">{r.field_name}</span>
                <span className="inline-flex items-center gap-1 text-xs font-black uppercase bg-emerald-600 text-white px-2 py-1 rounded"><ShieldCheck size={14} weight="fill" /> Verified</span>
              </div>
              <div className="font-mono text-3xl font-bold text-slate-900 mt-1">{r.value} <span className="text-lg text-slate-500">{r.unit}</span></div>
              <div className="mt-2"><TargetChip v={r} /></div>
              {r.source_page && <div className="font-body text-xs text-slate-500 mt-2">Source page/section: {r.source_page}</div>}
              {r.is_safety_critical && <div className="mt-1 text-xs font-bold uppercase text-red-600">Safety-critical value</div>}
            </div>
          ))}
          {data.results.length === 0 && (
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <p className="font-body text-slate-600">No verified structured data found for “{data.query}”.</p>
              <Button data-testid="search-manuals" className="mt-3 bg-slate-700 hover:bg-slate-600 font-semibold" onClick={() => window.location.assign("/documents")}><Books size={18} weight="bold" className="mr-1" /> Search Manuals</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldsManager({ fields, reload }) {
  const [f, setF] = useState({ name: "", category: "", default_unit: "", is_safety_critical: false });
  const add = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    try { await api.post("/quickdata/fields", f); setF({ name: "", category: "", default_unit: "", is_safety_critical: false }); reload(); toast.success("Field created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="qd-fields">
      <h3 className="font-body font-semibold text-slate-800 mb-2">Data fields</h3>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {fields.map((x) => (
          <span key={x.id} className="text-xs bg-slate-100 rounded px-2 py-1 font-body">
            {x.name}{x.default_unit ? ` (${x.default_unit})` : ""}{x.is_safety_critical ? " ⚠" : ""}
          </span>
        ))}
        {fields.length === 0 && <span className="text-slate-400 text-sm">No fields yet.</span>}
      </div>
      <div className="grid sm:grid-cols-4 gap-2 border-t border-slate-100 pt-3">
        <Input data-testid="qd-field-name" placeholder="Name e.g. Wheel torque" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="qd-field-cat" placeholder="Category e.g. Tyres" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        <Input data-testid="qd-field-unit" placeholder="Unit e.g. Nm" value={f.default_unit} onChange={(e) => setF({ ...f, default_unit: e.target.value })} />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs flex-1"><input type="checkbox" data-testid="qd-field-safety" checked={f.is_safety_critical} onChange={(e) => setF({ ...f, is_safety_critical: e.target.checked })} />Safety-critical</label>
          <Button data-testid="qd-field-add" onClick={add} className="h-9 bg-slate-900 font-semibold"><Plus size={14} weight="bold" /></Button>
        </div>
      </div>
    </div>
  );
}

const EMPTY = { field_id: "", scope_type: "asset_type", scope_id: "", asset_ids: [], value: "", unit: "", source_page: "" };
const scopeIdOf = (x) => x.scope_type === "asset_type" ? x.asset_type_id
  : x.scope_type === "manufacturer" ? x.manufacturer_id
  : x.scope_type === "model" ? x.asset_model_id
  : x.scope_type === "asset" ? x.asset_id : "";

function ValuesManager({ fields }) {
  const [types, setTypes] = useState([]);
  const [manus, setManus] = useState([]);
  const [models, setModels] = useState([]);
  const [assets, setAssets] = useState([]);
  const [v, setV] = useState(EMPTY);
  const [editing, setEditing] = useState(null);       // value being edited (or null = add mode)
  const [preview, setPreview] = useState(null);       // {count, assets}
  const [list, setList] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [history, setHistory] = useState(null);       // {rows, title}

  useEffect(() => {
    api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/config/manufacturers").then((r) => setManus(r.data)).catch(() => {});
    api.get("/config/asset-models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    api.get("/quickdata/values").then((r) => setList(r.data)).catch(() => {});
    api.get("/quickdata/values?archived=true").then((r) => setArchived(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const previewAffected = useCallback(async () => {
    if (!v.field_id || (v.scope_type !== "assets" && !v.scope_id) || (v.scope_type === "assets" && v.asset_ids.length === 0)) { setPreview(null); return; }
    try {
      const params = new URLSearchParams({ field_id: v.field_id, scope_type: v.scope_type });
      if (v.scope_type === "assets") params.set("asset_ids", v.asset_ids.join(","));
      else params.set("scope_id", v.scope_id);
      if (editing) params.set("exclude_value_id", editing.id);
      setPreview((await api.get(`/quickdata/inherit-count?${params}`)).data);
    } catch (e) { setPreview(null); }
  }, [v.field_id, v.scope_type, v.scope_id, v.asset_ids, editing]);
  useEffect(() => { previewAffected(); }, [previewAffected]);

  const startEdit = (x) => {
    setEditing(x);
    setV({ field_id: x.field_id, scope_type: x.scope_type, scope_id: scopeIdOf(x),
      asset_ids: x.asset_ids || [], value: x.value || "", unit: x.unit || "", source_page: x.source_page || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setV(EMPTY); setPreview(null); };

  const buildPayload = () => {
    const p = { field_id: v.field_id, scope_type: v.scope_type, value: v.value, unit: v.unit, source_page: v.source_page };
    if (v.scope_type === "asset_type") p.asset_type_id = v.scope_id;
    if (v.scope_type === "manufacturer") p.manufacturer_id = v.scope_id;
    if (v.scope_type === "model") p.asset_model_id = v.scope_id;
    if (v.scope_type === "asset") p.asset_id = v.scope_id;
    if (v.scope_type === "assets") p.asset_ids = v.asset_ids;
    return p;
  };

  const save = async () => {
    if (!v.field_id || !v.value.trim()) { toast.error("Field and value required"); return; }
    try {
      if (editing) {
        const r = await api.put(`/quickdata/values/${editing.id}`, buildPayload());
        toast.success(r.data.created_revision ? "New revision created — awaiting verification" : "Value updated");
        cancelEdit();
      } else {
        await api.post("/quickdata/values", buildPayload());
        toast.success("Value saved");
        setV({ ...EMPTY, field_id: v.field_id, scope_type: v.scope_type });
      }
      loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const verify = async (id) => { try { await api.post(`/quickdata/values/${id}/verify`); loadList(); toast.success("Verified"); } catch (e) { toast.error("Failed"); } };
  const archive = async (x) => {
    if (!window.confirm(`Archive "${x.field_name}" (${x.target_display})? It stays in history and can be restored.`)) return;
    try { await api.post(`/quickdata/values/${x.id}/archive`); loadList(); toast.success("Archived"); } catch (e) { toast.error("Failed"); }
  };
  const restore = async (x) => { try { await api.post(`/quickdata/values/${x.id}/restore`); loadList(); toast.success("Restored"); } catch (e) { toast.error("Failed"); } };
  const openHistory = async (x) => {
    try { const r = await api.get(`/quickdata/values/${x.id}/history`); setHistory({ rows: r.data, title: `${x.field_name} — ${x.target_display}` }); }
    catch (e) { toast.error("Failed to load history"); }
  };

  const scopeOptions = () => {
    if (v.scope_type === "asset_type") return types.map((t) => [t.id, t.name]);
    if (v.scope_type === "manufacturer") return manus.map((m) => [m.id, m.name]);
    if (v.scope_type === "model") return models.map((m) => [m.id, `${m.name}${m.variant ? " " + m.variant : ""}`]);
    if (v.scope_type === "asset") return assets.map((a) => [a.id, `#${a.asset_number} ${a.name}${a.registration ? " · " + a.registration : ""}`]);
    return [];
  };

  const editingVerified = editing && editing.verification_status === "verified";

  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4 flex flex-col gap-3" data-testid="qd-values">
      <div className="flex items-center justify-between">
        <h3 className="font-body font-semibold text-slate-800">{editing ? "Edit value" : "Add value"}</h3>
        {editing && <button data-testid="qd-cancel-edit" onClick={cancelEdit} className="text-xs font-semibold text-slate-500 underline">Cancel edit</button>}
      </div>

      {editingVerified && (
        <div className="text-sm font-body bg-amber-50 border border-amber-300 rounded px-3 py-2 text-amber-800" data-testid="qd-revision-warning">
          This value is <strong>verified</strong>. Changing the value, unit, target or source creates a <strong>new revision (awaiting verification)</strong>; the current value stays live until an authorised person verifies the new one. Historic checks keep the exact value they used.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <select data-testid="qd-val-field" className="h-10 border-2 border-slate-300 rounded px-2" value={v.field_id} onChange={(e) => setV({ ...v, field_id: e.target.value })}>
          <option value="">Field…</option>{fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select data-testid="qd-val-scope" className="h-10 border-2 border-slate-300 rounded px-2" value={v.scope_type} onChange={(e) => setV({ ...v, scope_type: e.target.value, scope_id: "", asset_ids: [] })}>
          {SCOPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {v.scope_type === "assets" ? (
          <select data-testid="qd-val-assets" multiple className="border-2 border-slate-300 rounded px-2 h-28 sm:col-span-2"
            value={v.asset_ids} onChange={(e) => setV({ ...v, asset_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
            {assets.map((a) => <option key={a.id} value={a.id}>#{a.asset_number} {a.name}{a.registration ? ` · ${a.registration}` : ""}</option>)}
          </select>
        ) : (
          <select data-testid="qd-val-scopeid" className="h-10 border-2 border-slate-300 rounded px-2 sm:col-span-2" value={v.scope_id} onChange={(e) => setV({ ...v, scope_id: e.target.value })}>
            <option value="">Choose {v.scope_type}…</option>{scopeOptions().map(([id, lbl]) => <option key={id} value={id}>{lbl}</option>)}
          </select>
        )}
        <Input data-testid="qd-val-value" placeholder="Value e.g. 190" value={v.value} onChange={(e) => setV({ ...v, value: e.target.value })} />
        <Input data-testid="qd-val-unit" placeholder="Unit e.g. Nm" value={v.unit} onChange={(e) => setV({ ...v, unit: e.target.value })} />
        <Input data-testid="qd-val-page" className="sm:col-span-2" placeholder="Source page / section (optional)" value={v.source_page} onChange={(e) => setV({ ...v, source_page: e.target.value })} />
      </div>

      {preview != null && (
        preview.count === 0 ? (
          <div data-testid="qd-inherit-count" className="text-sm font-body bg-amber-50 border border-amber-300 rounded px-3 py-2 text-amber-800">
            <strong>0 assets match this scope right now.</strong> This value will save, but <strong>it won't appear on any vehicle</strong> until at least one asset matches.
            {v.scope_type === "model" && <div className="mt-1">No vehicles are linked to this model yet. Open each vehicle → <em>Edit asset</em> → <em>Model (catalogue)</em> and pick this model. Or scope the value to the <strong>Asset type</strong> or to <strong>Selected assets</strong> instead.</div>}
            {v.scope_type === "manufacturer" && <div className="mt-1">No vehicles are linked to a model of this manufacturer yet. Link each vehicle to a catalogue model (Edit asset → Model (catalogue)), or scope to Asset type / Selected assets.</div>}
            {v.scope_type === "assets" && <div className="mt-1">The selected assets already have a more specific override for this field.</div>}
          </div>
        ) : (
          <div data-testid="qd-inherit-count" className="text-sm font-body bg-blue-50 border border-blue-200 rounded px-3 py-2 text-blue-800">
            <div><strong>{preview.count}</strong> {preview.count === 1 ? "asset" : "assets"} will be affected (those without a more specific override).</div>
            {preview.assets && preview.assets.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {preview.assets.map((a) => <span key={a.id} className="text-[11px] bg-white border border-blue-200 rounded px-1.5 py-0.5">#{a.asset_number} {a.name}{a.registration ? ` · ${a.registration}` : ""}</span>)}
              </div>
            )}
          </div>
        )
      )}
      <Button data-testid="qd-val-save" onClick={save} className="bg-blue-600 hover:bg-blue-700 font-semibold self-start">
        <Plus size={16} weight="bold" className="mr-1" />{editing ? "Save changes" : "Save value"}
      </Button>

      <div className="border-t border-slate-100 pt-3">
        <h4 className="font-body font-semibold text-slate-700 mb-2 text-sm">Existing values</h4>
        <div className="flex flex-col gap-1.5">
          {list.length === 0 && <p className="text-slate-400 text-sm">No values yet.</p>}
          {list.map((x) => (
            <div key={x.id} data-testid={`qd-value-${x.id}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-2 flex-wrap">
              <span className="font-body font-semibold text-slate-800">{x.field_name}</span>
              <span className="font-mono text-slate-900">{x.value}{x.unit ? ` ${x.unit}` : ""}</span>
              <TargetChip v={x} />
              <StatusChip status={x.verification_status} />
              <div className="ml-auto flex items-center gap-2">
                {x.verification_status !== "verified" && <button data-testid={`qd-verify-${x.id}`} onClick={() => verify(x.id)} className="text-emerald-700 font-bold inline-flex items-center gap-1"><CheckCircle size={15} weight="bold" />Verify</button>}
                <button data-testid={`qd-edit-${x.id}`} onClick={() => startEdit(x)} className="text-blue-700 font-bold inline-flex items-center gap-1"><PencilSimple size={15} weight="bold" />Edit</button>
                <button data-testid={`qd-history-${x.id}`} onClick={() => openHistory(x)} className="text-slate-600 font-bold inline-flex items-center gap-1"><ClockCounterClockwise size={15} weight="bold" />History</button>
                <button data-testid={`qd-archive-${x.id}`} onClick={() => archive(x)} className="text-red-600 font-bold inline-flex items-center gap-1"><Archive size={15} weight="bold" />Archive</button>
              </div>
            </div>
          ))}
        </div>

        {archived.length > 0 && (
          <div className="mt-4">
            <button data-testid="qd-toggle-archived" onClick={() => setShowArchived((s) => !s)} className="text-xs font-bold uppercase text-slate-500 inline-flex items-center gap-1">
              <Archive size={13} weight="bold" />Archived values ({archived.length}) {showArchived ? "▾" : "▸"}
            </button>
            {showArchived && (
              <div className="flex flex-col gap-1.5 mt-2">
                {archived.map((x) => (
                  <div key={x.id} data-testid={`qd-archived-${x.id}`} className="flex items-center gap-2 text-sm bg-slate-100 rounded px-2 py-2 flex-wrap opacity-80">
                    <span className="font-body font-semibold text-slate-600 line-through">{x.field_name}</span>
                    <span className="font-mono text-slate-500">{x.value}{x.unit ? ` ${x.unit}` : ""}</span>
                    <TargetChip v={x} />
                    <div className="ml-auto flex items-center gap-2">
                      <button data-testid={`qd-history-arch-${x.id}`} onClick={() => openHistory(x)} className="text-slate-600 font-bold inline-flex items-center gap-1"><ClockCounterClockwise size={15} weight="bold" />History</button>
                      <button data-testid={`qd-restore-${x.id}`} onClick={() => restore(x)} className="text-emerald-700 font-bold inline-flex items-center gap-1"><ArrowCounterClockwise size={15} weight="bold" />Restore</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-lg">Revision history</DialogTitle></DialogHeader>
          <p className="font-body text-sm text-slate-600">{history?.title}</p>
          <div className="flex flex-col gap-2 mt-2">
            {(history?.rows || []).map((r) => (
              <div key={r.id} data-testid={`qd-hist-${r.id}`} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 text-sm">
                <span className="font-mono font-bold text-slate-500">Rev {r.revision}</span>
                <span className="font-mono text-slate-900">{r.value}{r.unit ? ` ${r.unit}` : ""}</span>
                <span className="ml-auto"><StatusChip status={r.verification_status} /></span>
              </div>
            ))}
            {history?.rows?.length === 0 && <p className="text-slate-400 text-sm">No history.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function QuickDataPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("maintenance", "supervisor", "administrator");
  const [tab, setTab] = useState("search");
  const [fields, setFields] = useState([]);
  const loadFields = useCallback(() => { api.get("/quickdata/fields").then((r) => setFields(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadFields(); }, [loadFields]);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Quick Maintenance Data</h1>
        <p className="font-body text-slate-600 text-sm">Verified routine & safety-critical data — oils, pressures, torques, service intervals. Values inherit down the hierarchy: asset type → manufacturer → model / variant → selected assets → single asset override. Each value clearly shows exactly what it applies to.</p>
      </div>
      {canManage && (
        <div className="flex gap-2 border-b border-slate-200">
          {[["search", "Search"], ["manage", "Manage data"]].map(([k, l]) => (
            <button key={k} data-testid={`qd-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 font-body font-semibold text-sm -mb-px border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "search" && <SearchTab />}
      {tab === "manage" && canManage && (
        <div className="flex flex-col gap-4">
          <FieldsManager fields={fields} reload={loadFields} />
          <ValuesManager fields={fields} />
        </div>
      )}
    </div>
  );
}
